import { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { validateAddress } from "lib/api/subscription";
import SubscriptionsProvider, {
  SubscriptionsContext,
  SubscriptionsDispatchContext,
} from "contexts/SubscriptionsProvider";
import { CheckoutContext } from "contexts/CheckoutProvider";
import { getPossessiveName } from "utils/grammar";
import { getFirstAddressFromSubscriptions } from "utils/address";
import Modal from "@/atoms/account/Modal";
import { ShippingAddress } from "components/pages/account/subscription/ShippingAddress";

const ShippingModal = styled(Modal)`
  .modal-content-wrapper {
    padding: 2rem;
    background: ${({ theme }) => theme.colors["white"]};

    .styled-btn {
      margin-top: 1rem;
    }

    .card {
      box-shadow: none;
      padding: 0;
    }
  }
`;

const getCardTitle = (subName) =>
  subName
    ? `Where is ${getPossessiveName(subName)} book box going?`
    : "Where is this book box going?";

/**
 * Allows a user to save an address for a specific subscription via a modal.
 *
 * @note - this component subscribes to various sources for address pre-fill info
 * @note - we default country to US for new addresses
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.shippingAddressProps] - pass through props
 * @param {Number} [props.subId] - use if subscription context not available
 * @param {boolean} [props.isOpen]
 */
const ShippingAddressModal = ({
  shippingAddressProps,
  subId,
  ...modalProps
}) => {
  const { saveShippingAddress, subscription, subscriptions } =
    useContext(SubscriptionsContext);

  const { getSubscription, getSubscriptions } = useContext(
    SubscriptionsDispatchContext
  );

  const { session } = useContext(CheckoutContext);

  useEffect(() => {
    if (subId) {
      getSubscription({ subId });
      getSubscriptions();
    }
  }, [subId]);

  const { subShippingAddress, onSuccess, ...shippingAddrProps } =
    shippingAddressProps || {};
  const { childName, activeBox, shippingAddress } = subscription || {};
  const targetSubAddress = shippingAddress || activeBox?.shippingAddress;
  const { defaultAddress } = session || {};
  const firstSubAddress = getFirstAddressFromSubscriptions(subscriptions);

  // Order:
  //  1. use subShippingAddress prop if provided
  //  2. use address for passed subId
  //  3. use defaultAddress if in session (inject childName)
  //  4. use the first address found in subs (inject childName)
  //  5. prefill country only
  const selectedShippingAddress = subShippingAddress
    ? { ...subShippingAddress, name: childName || "" }
    : targetSubAddress
    ? targetSubAddress
    : defaultAddress
    ? { ...defaultAddress, name: childName || "" }
    : firstSubAddress
    ? { ...firstSubAddress, name: childName || "" }
    : { country: "US" };

  const handleSave = (address) => saveShippingAddress?.({ address, subId });

  const handleSuccess = () => {
    onSuccess?.();
  };

  return (
    <ShippingModal {...modalProps} suppressCloseButton id="shipping-modal">
      <ShippingAddress
        cardTitle={getCardTitle(childName)}
        ctaLabel="Save Shipping Info"
        onSave={handleSave}
        onSubmit={validateAddress}
        onSuccess={handleSuccess}
        shippingAddress={selectedShippingAddress}
        {...shippingAddrProps}
      />
    </ShippingModal>
  );
};

ShippingAddressModal.propTypes = {
  shippingAddressProps: PropTypes.object,
  subId: PropTypes.number,
};

/**
 * Connects modal to Subscriptions Provider for api calls and existing address data.
 *
 * @param {Object} props - pass through props
 * @param {Object} [props.shippingAddressProps] - pass through props
 * @param {Number} [props.subId] - use if subscription context not available
 * @param {boolean} [props.isOpen]
 */
const ShippingAddressModalWithProviders = (props) => (
  <SubscriptionsProvider>
    <ShippingAddressModal {...props} />
  </SubscriptionsProvider>
);

export default ShippingAddressModalWithProviders;
